<script>
// https://lucasleandro1204.github.io/vue-content-loading
import VueContentLoading from 'vue-content-loading'
// https://lodash.com/docs/4.17.11#groupBy
import _groupBy from 'lodash/groupBy'
import _values from 'lodash/values'
import formatDate, { dateDiffInMinutes } from '@utils/format-date'

export default {
  components: {
    VueContentLoading,
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
    dateTimes: {
      type: [Array, Object],
      default: () => [],
    },
    dateLoading: {
      type: Boolean,
      default: false,
    },
    selectDate: {
      type: Date,
      required: true,
    },
    selectTimeSession: {
      type: String,
      default: 'all',
      validator(value) {
        return ['all', 'morning', 'afternoon', 'night'].includes(value)
      },
    },
  },
  data() {
    return {
      timeSessionData: Object.freeze({
        all: this.$t('global.ALL_DAY' /* 整天 */),
        morning: this.$t('global.MORNING' /* 上午 */),
        afternoon: this.$t('global.AFTERNOON' /* 下午 */),
        night: this.$t('global.EVENING' /* 晚上 */),
      }),
      displayTimesMax: 4,
      defaultImage: require('@assets/images/activity_default.png'),
      handlingToProduct: false,
    }
  },
  computed: {
    sessionGroups() {
      return Object.keys(this.openTimesGroupBySession).filter(
        (group) => group !== 'all'
      );
    },
    openTimesGroupBySession() {
      const { times } = this.dateTimes.find(
        ({ date }) => date === formatDate(this.selectDate)
      ) || [];

      const timesArray = _values(times) || [];

      const checkTimeIsPast = (dateTime) => {
        const diffFromNow = dateDiffInMinutes(
          formatDate(new Date(), 'dateTime'),
          dateTime
        );
        return diffFromNow < 0
      };

      const openTimesArray =
        (times &&
          timesArray.filter(
            ({ capacity, startTime }) =>
              capacity === 'open' &&
              !checkTimeIsPast(`${formatDate(this.selectDate)} ${startTime}:00`)
          )) ||
        [];

      // groupBySession
      return {
        ..._groupBy(openTimesArray, function getTimeSession(time) {
          const [hour] = time.startTime.split(':');

          // timeSession
          return hour < 12 ? 'morning' : hour <= 17 ? 'afternoon' : 'night'
        }),
        all: openTimesArray,
      }


    },
    currentTimes() {
      const times = this.openTimesGroupBySession[this.selectTimeSession] || [];

      return times.filter(time => {
        return this.checkTimeIsAvailable(time)
      })
    },
    displayTimes() {
      const displayMax = this.displayTimesMax;

      return this.currentTimes && this.currentTimes.length < displayMax
        ? this.currentTimes
        : this.currentTimes.slice(0, displayMax)
    },
  },
  watch: {
    sessionGroups(v, v2) {
      if(v.length === v2.length && v2.length === 0) {

      }else {
        this.$emit('update-sessions', {
          productKey: this.data.alias,
          sessionArray: this.sessionGroups,
        })
      }
    },
  },
  methods: {
    handleToProduct(product, selectedTime = {}) {
      if (!this.handlingToProduct) {
        this.$emit('select-time', { product, selectedTime });
        this.handlingToProduct = true
      }
    },
    checkTimeIsAvailable(time) {
      const checkTimeIsPast = (dateTime) => {
        const diffFromNow = dateDiffInMinutes(
          formatDate(new Date(), 'dateTimeSecond'),
          dateTime
        );
        return diffFromNow < 0
      };

      return (
        time.capacity === 'open' &&
        !checkTimeIsPast(
          `${formatDate(this.selectDate)} ${time.startTime}:00`
        )
      )
    },
  },
}
</script>
<template>
  <div :class="$style.wrapper" @click="handleToProduct(data)">
    <div :class="$style.inner">
      <div class="columns is-multiline is-mobile">
        <div class="column is-full-mobile is-one-third-tablet has-text-centered">
          <picture @click.stop="handleToProduct(data)">
            <BaseImage
              :src="data.introimage"
              :error-src="defaultImage"
              :class="$style.image"
              class-name="is-cover"
            />
          </picture>
        </div>
        <div :class="$style.content" class="column is-full-mobile">
          <div>
            <div :class="$style['name']" @click.stop="handleToProduct(data)">{{ data.name }}</div>
            <div>{{ data.address }}</div>
          </div>
          <div v-if="dateTimes.length > 0" :class="$style['buttons']" class="buttons">
            <b-button
              v-for="time in displayTimes"
              :key="time.alias"
              type="is-primary"
              rounded
              outlined
              @click.stop="handleToProduct(data, time)"
            >{{ time.startTime }}</b-button>
            <b-button
              v-show="displayTimes.length === 0"
              type="is-light"
              rounded
              disabled
            >{{ timeSessionData[selectTimeSession] }}{{ $t('global.NULL_RESERVATION_TIME'/* 無可預訂時段 */) }}</b-button>
            <b-button
              v-if="currentTimes.length > displayTimesMax"
              type="is-primary is-circle is-outlined"
              @click.stop="handleToProduct(data)"
            >
              <BaseIcon name="ellipsis-h" />
            </b-button>
          </div>
          <VueContentLoading
            v-else-if="dateLoading"
            :class="[$style['buttons'] , $style['loading']]"
            :width="357"
            :height="45"
            :speed="3"
          >
            <rect x="0" y="0" rx="23" ry="23" width="90" height="45" />
            <rect x="100" y="0" rx="23" ry="23" width="90" height="45" />
            <rect x="200" y="0" rx="23" ry="23" width="90" height="45" />
          </VueContentLoading>
          <div v-else class="buttons">
            <b-button
              type="is-light"
              rounded
              disabled
            >{{ timeSessionData[selectTimeSession] }}{{ $t('global.NULL_RESERVATION_TIME'/* 無可預訂時段 */) }}</b-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
.wrapper {
  &:hover {
    cursor: pointer;
    transition: box-shadow 0.3s;
    box-shadow: 0 0 15px 0 rgba(178, 180, 181, 0.3);
    .name {
      color: $color-primary;
    }
  }
  + .wrapper .inner {
    border-top: 1px solid $color-border-base;
  }
}
.inner {
  padding: 1rem;
  @include desktop {
    padding: 1rem 0 0.9rem;
    margin: 0 1.5rem;
  }
  .content {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
}
.image {
  height: 162px;
  width: 262px;
  overflow: hidden;
  background: $grey-lighter no-repeat;
  border-radius: $size-image-border-radius;
  cursor: pointer;
  position: relative;
  background-size: cover;
  .icon {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    color: white;
    height: 2em;
    width: 2em;
  }
}
.name {
  font-size: 1.3rem;
  margin-bottom: 0.5rem;
  line-height: 1.2em;
  //@extend %typography-medium;
  font-weight: 500;
  color: $color-heading-text;
  cursor: pointer;
}
.loading {
  max-width: 360px;
}
.buttons {
  padding-bottom: 5px;
  @include mobile {
    margin-top: 15px;
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
  }
}
</style>
